import { MediaKind } from "services/menu/restModels";

/*
 * Правила загрузки файлов кабинета — одно место на весь проект.
 * Потолки продублированы на бэке (UploadPresets): здесь они дают понятную ошибку
 * до отправки файла, там — страховка от обхода браузерной проверки.
 */

const MB = 1024 * 1024;

const IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const GIF_MIME_TYPES = ["image/gif"];
const VIDEO_MIME_TYPES = ["video/mp4", "video/webm"];

export const MENU_ITEM_MEDIA_ACCEPT = [
    ...IMAGE_MIME_TYPES,
    ...GIF_MIME_TYPES,
    ...VIDEO_MIME_TYPES,
].join(",");

export const MENU_ITEM_MEDIA_HINT = "jpg, png, gif, mp4, webm · до 10 МБ, видео до 20 МБ";

export const UNSUPPORTED_MEDIA_ERROR = "Поддерживаются только jpg, png, gif, mp4 и webm";

const MAX_BYTES: Record<MediaKind, number> = {
    [MediaKind.image]: 10 * MB,
    [MediaKind.gif]: 10 * MB,
    [MediaKind.video]: 20 * MB,
};

const MAX_VIDEO_SECONDS = 30;

export const detectMediaKind = (file: File): MediaKind | null => {
    const mime = file.type.toLowerCase();

    if (IMAGE_MIME_TYPES.indexOf(mime) >= 0) return MediaKind.image;
    if (GIF_MIME_TYPES.indexOf(mime) >= 0) return MediaKind.gif;
    if (VIDEO_MIME_TYPES.indexOf(mime) >= 0) return MediaKind.video;

    return null;
};

export const MENU_COVER_MAX_BYTES = 10 * MB;
export const INFO_MESSAGE_IMAGE_MAX_BYTES = 10 * MB;
export const MENU_LOGO_MAX_BYTES = 5 * MB;
export const SUPPLIER_LOGO_MAX_BYTES = 5 * MB;

export const IMAGE_UPLOAD_ACCEPT = IMAGE_MIME_TYPES.join(",");

export const imageUploadHint = (maxBytes: number) =>
    `jpg, png · до ${Math.round(maxBytes / MB)} МБ`;

/** Проверяет обычную картинку — обложку или логотип. Возвращает текст ошибки либо null. */
export const validateImageFile = (file: File, maxBytes: number): string | null => {
    if (detectMediaKind(file) !== MediaKind.image) {
        return "Поддерживаются только jpg и png";
    }

    if (file.size > maxBytes) {
        return `Файл больше ${Math.round(maxBytes / MB)} МБ`;
    }

    return null;
};

const readVideoDuration = (file: File): Promise<number | null> =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");

        const finish = (duration: number | null) => {
            URL.revokeObjectURL(url);
            resolve(duration);
        };

        video.preload = "metadata";
        // Битый или экзотический контейнер не досылает loadedmetadata, а Safari умеет
        // отдать Infinity — без обеих проверок промис повиснет и кнопка останется заблокированной.
        video.onloadedmetadata = () =>
            finish(isFinite(video.duration) ? video.duration : null);
        video.onerror = () => finish(null);
        video.src = url;
    });

export interface MediaValidationResult {
    kind: MediaKind | null;
    error: string | null;
}

/** Возвращает тип медиа либо готовый для тоста текст ошибки. */
export const validateMenuItemMedia = async (file: File): Promise<MediaValidationResult> => {
    const kind = detectMediaKind(file);

    if (kind === null) {
        return { kind: null, error: UNSUPPORTED_MEDIA_ERROR };
    }

    const maxBytes = MAX_BYTES[kind];

    if (file.size > maxBytes) {
        return { kind, error: `Файл больше ${Math.round(maxBytes / MB)} МБ` };
    }

    if (kind === MediaKind.video) {
        const duration = await readVideoDuration(file);

        if (duration === null) {
            return { kind, error: "Не удалось прочитать видео — попробуйте mp4 (H.264) или webm" };
        }

        if (duration > MAX_VIDEO_SECONDS) {
            return { kind, error: `Видео длиннее ${MAX_VIDEO_SECONDS} секунд` };
        }
    }

    return { kind, error: null };
};
